import { Component, OnInit } from '@angular/core';
import { TrainerService } from '../../../../@shared/services/trainer.service';
import { TrainerApplicationResponse } from '../../../../@shared/models/response.module';
import { ToastService } from '../../../../@core/services/toast.service';

@Component({
  selector: 'app-admin-applications',
  standalone: false,
  templateUrl: './admin-applications.html',
  styleUrl: './admin-applications.css'
})
export class AdminApplications implements OnInit {
  loading: boolean = true;
  applications: TrainerApplicationResponse[] = [];

  // Pagination
  currentPage: number = 1;
  perPage: number = 20;
  totalItems: number = 0;
  totalPages: number = 0;
  Math = Math;

  // Filters
  statusFilter: string = '';

  // Expand
  expandedId: string | null = null;

  // Reject modal
  showRejectModal: boolean = false;
  rejectingApplication: TrainerApplicationResponse | null = null;
  rejectionReason: string = '';
  actionLoading: boolean = false;

  constructor(
    private trainerService: TrainerService,
    private toastService: ToastService
  ) {}

  ngOnInit() {
    this.loadApplications();
  }

  loadApplications() {
    this.loading = true;
    const params: { page?: number; per_page?: number; status?: string } = {
      page: this.currentPage,
      per_page: this.perPage
    };
    if (this.statusFilter) {
      params.status = this.statusFilter;
    }

    this.trainerService.getApplications(params).subscribe({
      next: (response: any) => {
        if (Array.isArray(response)) {
          this.applications = response;
          this.totalItems = response.length;
          this.totalPages = 1;
        } else {
          this.applications = response.data || response;
          this.totalItems = response.total_items || this.applications.length;
          this.totalPages = response.total_pages || 1;
        }
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      }
    });
  }

  onFilterChange() {
    this.currentPage = 1;
    this.loadApplications();
  }

  onPageChange(page: number) {
    this.currentPage = page;
    this.loadApplications();
  }

  toggleExpand(applicationId: string) {
    this.expandedId = this.expandedId === applicationId ? null : applicationId;
  }

  approveApplication(app: TrainerApplicationResponse) {
    this.actionLoading = true;
    this.trainerService.approveApplication(app.application_id as any).subscribe({
      next: () => {
        this.toastService.success('Application approved successfully');
        this.actionLoading = false;
        this.loadApplications();
      },
      error: () => {
        this.toastService.error('Failed to approve application');
        this.actionLoading = false;
      }
    });
  }

  openRejectModal(app: TrainerApplicationResponse) {
    this.rejectingApplication = app;
    this.rejectionReason = '';
    this.showRejectModal = true;
  }

  closeRejectModal() {
    this.showRejectModal = false;
    this.rejectingApplication = null;
    this.rejectionReason = '';
  }

  confirmReject() {
    if (!this.rejectingApplication || !this.rejectionReason.trim()) return;
    this.actionLoading = true;
    this.trainerService.rejectApplication(
      this.rejectingApplication.application_id as any,
      this.rejectionReason.trim()
    ).subscribe({
      next: () => {
        this.toastService.success('Application rejected');
        this.actionLoading = false;
        this.closeRejectModal();
        this.loadApplications();
      },
      error: () => {
        this.toastService.error('Failed to reject application');
        this.actionLoading = false;
      }
    });
  }

  getStatusBadgeClass(status: string): string {
    switch (status) {
      case 'pending': return 'bg-warning-subtle text-warning';
      case 'approved': return 'bg-success-subtle text-success';
      case 'rejected': return 'bg-danger-subtle text-danger';
      default: return 'bg-secondary-subtle text-secondary';
    }
  }

  getPages(): number[] {
    const pages: number[] = [];
    const maxPages = 5;
    let start = Math.max(1, this.currentPage - Math.floor(maxPages / 2));
    let end = Math.min(this.totalPages, start + maxPages - 1);
    if (end - start + 1 < maxPages) {
      start = Math.max(1, end - maxPages + 1);
    }
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    return pages;
  }
}
